/**
 * Purchases API Service
 * 
 * Serviço para comunicação com a API do módulo de Compras.
 * Gerencia requisições, revisões, match fiscal, aprovações e rastreio.
 */

import api from './api'

// ============================================
// REQUISIÇÕES DE COMPRA
// ============================================
export const purchaseService = {
  // Listar todas as requisições
  getAll: async (filters = {}) => {
    const response = await api.get('/api/purchases', { params: filters })
    return response.data
  },

  // Buscar requisição por ID
  getById: async (id) => {
    const response = await api.get(`/api/purchases/${id}`)
    return response.data
  },

  // Criar nova requisição
  create: async (data) => {
    const response = await api.post('/api/purchases', data)
    return response.data
  },

  // Atualizar requisição
  update: async (id, data) => {
    const response = await api.put(`/api/purchases/${id}`, data)
    return response.data
  },

  // Atualizar status de uma requisição
  updateStatus: async (id, status) => {
    const response = await api.put(`/api/purchases/${id}/status`, { status })
    return response.data
  },

  // Deletar requisição
  delete: async (id) => {
    const response = await api.delete(`/api/purchases/${id}`)
    return response.data
  },

  // Baixar cotação (PDF ou JSON)
  downloadQuotation: async (id) => {
    try {
      // Tentar primeiro como blob (PDF)
      const response = await api.get(`/api/purchases/${id}/quotation/download`, {
        responseType: 'blob'
      })
      
      // Verificar se é JSON (content-type application/json)
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        // Se for JSON, converter para objeto
        const text = await response.data.text();
        return JSON.parse(text);
      }
      
      // Se for blob (PDF), retornar diretamente
      return response.data;
    } catch (error) {
      // Se falhar, tentar como JSON normal
      const response = await api.get(`/api/purchases/${id}/quotation/download`)
      return response.data
    }
  },

  // Enviar cotação por email
  sendQuotation: async (id, emailData = {}) => {
    const response = await api.post(`/api/purchases/${id}/quotation/send`, emailData)
    return response.data
  }
}

// ============================================
// REVISÃO TÉCNICA
// ============================================
export const reviewService = {
  // Listar requisições para revisão técnica
  getList: async () => {
    const response = await api.get('/api/purchases/review/list')
    return response.data
  },

  // Executar análise IA de uma requisição
  analyze: async (id) => {
    const response = await api.post(`/api/purchases/review/${id}/analyze`)
    return response.data
  },

  // Aprovar revisão técnica
  approve: async (id, approved = true, items = null) => {
    const payload = { approved }
    if (items && Array.isArray(items)) {
      payload.items = items
    }
    const response = await api.put(`/api/purchases/review/${id}/approve`, payload)
    return response.data
  }
}

// ============================================
// MATCH FISCAL
// ============================================
export const mirrorService = {
  // Listar requisições aguardando espelho
  getList: async () => {
    const response = await api.get('/api/purchases/mirrors')
    return response.data
  },

  // Buscar requisição por ID (para match fiscal)
  getById: async (id) => {
    const response = await api.get(`/api/purchases/mirrors/${id}`)
    return response.data
  },

  // Upload de arquivo PDF/XML
  upload: async (id, file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post(`/api/purchases/mirrors/${id}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  // Executar match fiscal
  verify: async (id, payload) => {
    // Aceitar tanto array (formato antigo) quanto objeto (novo formato com totalWithTaxes)
    const requestBody = Array.isArray(payload) 
      ? { invoiceItems: payload }
      : payload;
    const response = await api.post(`/api/purchases/mirrors/${id}/verify`, requestBody)
    return response.data
  },

  // Aprovar match fiscal e liberar para financeiro
  approve: async (id, supplierId = null, supplierData = null) => {
    const payload = {}
    if (supplierId) {
      payload.supplierId = supplierId
    }
    if (supplierData) {
      payload.supplierData = supplierData
    }
    const response = await api.post(`/api/purchases/mirrors/${id}/approve`, payload)
    return response.data
  }
}

// ============================================
// APROVAÇÃO FINANCEIRA
// ============================================
export const financeService = {
  // Listar requisições para aprovação financeira
  getList: async () => {
    const response = await api.get('/api/purchases/finance')
    return response.data
  },

  // Buscar requisição por ID (para aprovação)
  getById: async (id) => {
    const response = await api.get(`/api/purchases/finance/${id}`)
    return response.data
  },

  // Executar análise financeira
  analyze: async (id, useLocal = false) => {
    const url = `/api/purchases/finance/${id}/analyze${useLocal ? '?useLocal=true' : ''}`
    const response = await api.post(url)
    return response.data
  },

  // Aprovar financeiramente
  approve: async (id, approved = true, notes = null, createAudit = false) => {
    const response = await api.post(`/api/purchases/finance/${id}/approve`, { approved, notes, createAudit })
    return response.data
  },

  // Atualizar forma de pagamento
  updatePaymentMethod: async (id, paymentData) => {
    const response = await api.put(`/api/purchases/finance/${id}/payment-method`, paymentData)
    return response.data
  },

  // Atualizar datas de vencimento
  updatePaymentDates: async (id, payload) => {
    const response = await api.put(`/api/purchases/finance/${id}/payment-dates`, payload)
    return response.data
  },

  // Calcular parcelas (preview)
  calculateInstallments: async (id, paymentData) => {
    const response = await api.post(`/api/purchases/finance/${id}/calculate-installments`, paymentData)
    return response.data
  },

  // Buscar saldo de caixa
  getCashBalance: async (useProjected = false) => {
    const response = await api.get('/api/purchases/finance/cash-balance', {
      params: { useProjected }
    })
    return response.data
  }
}

// ============================================
// RASTREIO (FOLLOW-UP)
// ============================================
export const followUpService = {
  // Listar pedidos em rastreio
  getList: async () => {
    const response = await api.get('/api/purchases/follow-up')
    return response.data
  },

  // Buscar rastreio por ID
  getById: async (id) => {
    const response = await api.get(`/api/purchases/follow-up/${id}`)
    return response.data
  },

  // Atualizar status de rastreio
  update: async (id, data) => {
    const response = await api.put(`/api/purchases/follow-up/${id}`, data)
    return response.data
  }
}

// ============================================
// ESTOQUE MÍNIMO
// ============================================
export const minimumStockService = {
  // Upload de arquivo Excel
  upload: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/api/purchases/minimum-stock/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  // Listar registros
  getAll: async (filters = {}) => {
    const response = await api.get('/api/purchases/minimum-stock', { params: filters })
    return response.data
  },

  // Buscar por ID
  getById: async (id) => {
    const response = await api.get(`/api/purchases/minimum-stock/${id}`)
    return response.data
  },

  // Buscar por marca
  getByBrand: async (brand, dateFrom = null, dateTo = null) => {
    const params = {}
    if (dateFrom) params.dateFrom = dateFrom
    if (dateTo) params.dateTo = dateTo
    const response = await api.get(`/api/purchases/minimum-stock/by-brand/${brand}`, { params })
    return response.data
  },

  // Listar marcas
  getBrands: async () => {
    const response = await api.get('/api/purchases/minimum-stock/brands')
    return response.data
  },

  // Configuração de cálculo
  getConfig: async () => {
    const response = await api.get('/api/purchases/minimum-stock/config')
    return response.data
  },

  updateConfig: async (config) => {
    const response = await api.put('/api/purchases/minimum-stock/config', config)
    return response.data
  },

  // Analisar com cálculo estatístico
  analyze: async (brand, dateFrom = null, dateTo = null) => {
    const params = {}
    if (dateFrom) params.dateFrom = dateFrom
    if (dateTo) params.dateTo = dateTo
    const response = await api.post(`/api/purchases/minimum-stock/analyze/${encodeURIComponent(brand)}`, {}, { params })
    return response.data
  },

  // Analisar uma ou mais marcas
  analyzeBatch: async (brands, dateFrom = null, dateTo = null) => {
    const params = {}
    if (dateFrom) params.dateFrom = dateFrom
    if (dateTo) params.dateTo = dateTo
    const response = await api.post(
      '/api/purchases/minimum-stock/analyze-batch',
      { brands },
      { params }
    )
    return response.data
  },

  // Atualizar estoque mínimo de um produto
  updateProductMinStock: async (productCode, suggestedMinimum) => {
    const response = await api.put(`/api/purchases/minimum-stock/update/${productCode}`, {
      suggestedMinimum
    })
    return response.data
  },

  // Atualizar estoque mínimo de todos os produtos sugeridos (uma ou várias marcas)
  updateAllMinStock: async (_brand, suggestions) => {
    const response = await api.put('/api/purchases/minimum-stock/update-all', {
      suggestions
    })
    return response.data
  },

  // Deletar registro
  delete: async (id) => {
    const response = await api.delete(`/api/purchases/minimum-stock/${id}`)
    return response.data
  }
}